import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { useState, useEffect } from "react";
import "@/App.css";

// Theme Context
import { ThemeProvider } from "@/context/ThemeContext";

// Page Tracking Hook
import usePageTracking from "@/hooks/usePageTracking";

// Layouts
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import IntroScreen from "@/components/IntroScreen";

// Public Pages
import HomePage from "@/pages/HomePage";
import CountriesPage from "@/pages/CountriesPage";
import CountryDetailPage from "@/pages/CountryDetailPage";
import UniversitiesPage from "@/pages/UniversitiesPage";
import UniversityDetailPage from "@/pages/UniversityDetailPage";
import SchoolsPage from "@/pages/SchoolsPage";
import JobsPage from "@/pages/JobsPage";
import ComparePage from "@/pages/ComparePage";
import ServicesPage from "@/pages/ServicesPage";
import ArticlesPage from "@/pages/ArticlesPage";
import ArticleDetailPage from "@/pages/ArticleDetailPage";
import GalleryPage from "@/pages/GalleryPage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";

// Service Pages
import EducationConsultingPage from "@/pages/services/EducationConsultingPage";
import VisaServicePage from "@/pages/services/VisaServicePage";
import HotelServicePage from "@/pages/services/HotelServicePage";
import ResumeServicePage from "@/pages/services/ResumeServicePage";

// Admin Pages
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminCountries from "@/pages/admin/AdminCountries";
import AdminUniversities from "@/pages/admin/AdminUniversities";
import AdminJobs from "@/pages/admin/AdminJobs";
import AdminArticles from "@/pages/admin/AdminArticles";
import AdminTestimonials from "@/pages/admin/AdminTestimonials";
import AdminGallery from "@/pages/admin/AdminGallery";
import AdminEvents from "@/pages/admin/AdminEvents";
import AdminContent from "@/pages/admin/AdminContent";
import AdminPartners from "@/pages/admin/AdminPartners";
import AdminSchools from "@/pages/admin/AdminSchools";
import AdminConsultations from "@/pages/admin/AdminConsultations";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminManagement from "@/pages/admin/AdminManagement";
import CountriesManagement from "@/pages/admin/CountriesManagement";
import UniversitiesComprehensiveManagement from "@/pages/admin/UniversitiesComprehensiveManagement";
import SchoolsComprehensiveManagement from "@/pages/admin/SchoolsComprehensiveManagement";
import AnalyticsDashboard from "@/pages/admin/AnalyticsDashboard";

// Component to track page views
function PageTracker() {
  usePageTracking();
  return null;
}

function App() {
  const [showIntro, setShowIntro] = useState(true);
  const [hasSeenIntro, setHasSeenIntro] = useState(false);

  useEffect(() => {
    // Check if user has already seen intro in this session
    const introSeen = sessionStorage.getItem('introSeen');
    if (introSeen === 'true') {
      setShowIntro(false);
      setHasSeenIntro(true);
    }
  }, []);

  const handleIntroComplete = () => {
    setShowIntro(false);
    setHasSeenIntro(true);
    sessionStorage.setItem('introSeen', 'true');
  };

  return (
    <ThemeProvider>
      <div className="App min-h-screen bg-[#121212] text-white" dir="rtl">
        {showIntro && !hasSeenIntro && <IntroScreen onComplete={handleIntroComplete} />}
        
        <BrowserRouter>
          <PageTracker />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<><Header /><HomePage /><Footer /></>} />
          <Route path="/countries" element={<><Header /><CountriesPage /><Footer /></>} />
          <Route path="/countries/:id" element={<><Header /><CountryDetailPage /><Footer /></>} />
          <Route path="/universities" element={<><Header /><UniversitiesPage /><Footer /></>} />
          <Route path="/universities/:id" element={<><Header /><UniversityDetailPage /><Footer /></>} />
          <Route path="/schools" element={<><Header /><SchoolsPage /><Footer /></>} />
          <Route path="/jobs" element={<><Header /><JobsPage /><Footer /></>} />
          <Route path="/compare" element={<><Header /><ComparePage /><Footer /></>} />
          
          {/* Service Routes - Specific routes MUST come before generic /services route */}
          <Route path="/services/education-consulting" element={<><Header /><EducationConsultingPage /><Footer /></>} />
          <Route path="/services/visa" element={<><Header /><VisaServicePage /><Footer /></>} />
          <Route path="/services/hotel" element={<><Header /><HotelServicePage /><Footer /></>} />
          <Route path="/services/resume" element={<><Header /><ResumeServicePage /><Footer /></>} />
          <Route path="/services" element={<><Header /><ServicesPage /><Footer /></>} />
          
          <Route path="/articles" element={<><Header /><ArticlesPage /><Footer /></>} />
          <Route path="/articles/:id" element={<><Header /><ArticleDetailPage /><Footer /></>} />
          <Route path="/gallery" element={<><Header /><GalleryPage /><Footer /></>} />
          <Route path="/about" element={<><Header /><AboutPage /><Footer /></>} />
          <Route path="/contact" element={<><Header /><ContactPage /><Footer /></>} />
          
          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/countries" element={<AdminCountries />} />
          <Route path="/admin/universities" element={<AdminUniversities />} />
          <Route path="/admin/schools" element={<AdminSchools />} />
          <Route path="/admin/jobs" element={<AdminJobs />} />
          <Route path="/admin/articles" element={<AdminArticles />} />
          <Route path="/admin/testimonials" element={<AdminTestimonials />} />
          <Route path="/admin/gallery" element={<AdminGallery />} />
          <Route path="/admin/events" element={<AdminEvents />} />
          <Route path="/admin/content" element={<AdminContent />} />
          <Route path="/admin/partners" element={<AdminPartners />} />
          <Route path="/admin/consultations" element={<AdminConsultations />} />
          <Route path="/admin/admins" element={<AdminManagement />} />
          <Route path="/admin/countries-management" element={<CountriesManagement />} />
          <Route path="/admin/universities-management" element={<UniversitiesComprehensiveManagement />} />
          <Route path="/admin/schools-management" element={<SchoolsComprehensiveManagement />} />
          <Route path="/admin/analytics" element={<AnalyticsDashboard />} />
          <Route path="/admin/settings" element={<AdminSettings />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" />
    </div>
    </ThemeProvider>
  );
}

export default App;
